import React, { useContext, useEffect, useState } from 'react';
import { FaCreditCard, FaLock } from 'react-icons/fa';
import AuthContext from '../../../../utils/secure-route/AuthContext';
import useApi from '../../../../utils/api-manager/Helper/useApi';
import { useToastHandler } from '../../../common/ToastHandler';
import { CustomButton } from '../../../common/ReusableComponent/CustomButton';
import LoadingSpinner from '../Orders/LoadingSpinner';
import './PaymentGatewaySettings.css';

const emptyConfig = {
  provider: 'stripe',
  mode: 'test',
  enabled: false,
  cod_enabled: true,
  currency: 'INR',
  public_key: '',
  secret_key: '',
  webhook_secret: '',
  secret_configured: false,
  webhook_configured: false,
};

const PaymentGatewaySettings = () => {
  const { user } = useContext(AuthContext);
  const { Get, Post, getHost } = useApi();
  const { handleToast } = useToastHandler();
  const [config, setConfig] = useState(emptyConfig);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const storeId = user.store;
  const apiHost = getHost();

  useEffect(() => {
    const load = async () => {
      try {
        const data = await Get('paymentGatewayConfig');
        if (data) setConfig(prev => ({ ...prev, ...data, secret_key: '', webhook_secret: '' }));
      } catch (error) {
        handleToast({ type: 'ERROR', message: error.response?.data?.message || 'Unable to load payment settings.' });
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [storeId]);

  const update = (field, value) => setConfig(prev => ({ ...prev, [field]: value }));
  const selectProvider = (provider) => setConfig(prev => provider === prev.provider ? prev : ({
    ...prev,
    provider,
    public_key: '',
    secret_key: '',
    webhook_secret: '',
    secret_configured: false,
    webhook_configured: false,
    enabled: false,
  }));

  const save = async () => {
    if (config.enabled) {
      if (!config.public_key.trim()) {
        handleToast({ type: 'WARNING', message: 'Enter the public key or Razorpay key ID.' });
        return;
      }
      if (!config.secret_configured && !config.secret_key.trim()) {
        handleToast({ type: 'WARNING', message: 'Enter the gateway secret key.' });
        return;
      }
      if (!config.webhook_configured && !config.webhook_secret.trim()) {
        handleToast({ type: 'WARNING', message: 'Enter the webhook signing secret.' });
        return;
      }
    }
    setSaving(true);
    try {
      const payload = {
        store: storeId,
        provider: config.provider,
        mode: config.mode,
        enabled: config.enabled,
        cod_enabled: config.cod_enabled,
        currency: config.currency.toUpperCase(),
        public_key: config.public_key.trim(),
        ...(config.secret_key.trim() && { secret_key: config.secret_key.trim() }),
        ...(config.webhook_secret.trim() && { webhook_secret: config.webhook_secret.trim() }),
      };
      const response = await Post('paymentGatewayConfig', payload);
      setConfig(prev => ({
        ...prev,
        ...response.data,
        secret_key: '',
        webhook_secret: '',
      }));
      handleToast({ type: 'SUCCESS', message: 'Payment gateway configuration saved.' });
    } catch (error) {
      const data = error.response?.data;
      handleToast({ type: 'ERROR', message: data?.message || data?.non_field_errors?.[0] || 'Unable to save payment settings.' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <LoadingSpinner />;

  const webhookUrl = `${apiHost}/api/v1/payment_gateway/webhook/${config.provider}/?store_id=${storeId}`;
  const isStripe = config.provider === 'stripe';

  return (
    <div className="gateway-settings">
      {saving && <LoadingSpinner />}
      <div className="gateway-header">
        <div><span>PAYMENTS</span><h3><FaCreditCard /> Payment Gateway</h3></div>
        <p>Connect one merchant-owned gateway to this store. Credentials are encrypted and never returned after saving.</p>
      </div>

      <div className="gateway-grid">
        <label className={`gateway-provider ${isStripe ? 'selected' : ''}`}>
          <input type="radio" checked={isStripe} onChange={() => selectProvider('stripe')} />
          <strong>Stripe</strong><span>Cards and Stripe-enabled payment methods</span>
        </label>
        <label className={`gateway-provider ${!isStripe ? 'selected' : ''}`}>
          <input type="radio" checked={!isStripe} onChange={() => selectProvider('razorpay')} />
          <strong>Razorpay</strong><span>Cards, UPI and Razorpay-enabled methods</span>
        </label>
      </div>

      <div className="gateway-form">
        <div className="gateway-row">
          <label>Mode<select value={config.mode} onChange={e => update('mode', e.target.value)}><option value="test">Test</option><option value="live">Live</option></select></label>
          <label>Currency<input maxLength="3" value={config.currency} onChange={e => update('currency', e.target.value.toUpperCase())} /></label>
        </div>
        <fieldset disabled={!config.enabled} className="gateway-credentials">
          <legend>{config.enabled ? 'Online payment credentials' : 'Online payment credentials (enable online payments to configure)'}</legend>
          <label>{isStripe ? 'Publishable key' : 'Key ID'}<input value={config.public_key} onChange={e => update('public_key', e.target.value)} placeholder={isStripe ? 'pk_test_...' : 'rzp_test_...'} /></label>
          <label>Secret key<input type="password" value={config.secret_key} onChange={e => update('secret_key', e.target.value)} placeholder={config.secret_configured ? 'Saved — enter only to replace' : 'Enter secret key'} /></label>
          <label>Webhook signing secret<input type="password" value={config.webhook_secret} onChange={e => update('webhook_secret', e.target.value)} placeholder={config.webhook_configured ? 'Saved — enter only to replace' : 'Enter webhook secret'} /></label>
          <label>Webhook URL<input readOnly value={webhookUrl} onFocus={e => e.target.select()} /></label>
        </fieldset>
        <div className="gateway-checks">
          <label><input type="checkbox" checked={config.enabled} onChange={e => update('enabled', e.target.checked)} /> Enable online payment</label>
          <label><input type="checkbox" checked={config.cod_enabled} onChange={e => update('cod_enabled', e.target.checked)} /> Allow cash on delivery</label>
        </div>
        <div className="gateway-note"><FaLock /> COD can be enabled by itself. For online payments, configure the selected provider and its exact webhook URL, then enable it after a successful test payment.</div>
        <CustomButton text={saving ? 'Saving...' : 'Save Payment Gateway'} onClick={save} disabled={saving} />
      </div>
    </div>
  );
};

export default PaymentGatewaySettings;
